import {
  changePasswordRequestSchema,
  resetPasswordRequestSchema,
  type ResetPasswordRequest,
} from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { useApiClient } from '@/hooks/use-api-client';
import { centralLoginPath, forgotPasswordPath, RESET_EMAIL_PARAM } from '@/lib/auth-paths';
import { parseSignInRequest } from '@/lib/sign-in-request';

/** Same fallback reasoning as `login-page.tsx` — never a declared `ErrorCode`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/** The one field that exists on this form and not in the contract. */
const CONFIRM_FIELD = 'confirmNewPassword';

/**
 * The contract's shape plus the confirmation field.
 *
 * `packages/contracts` is deliberately NOT extended: "type it twice" is a
 * confirmation box in a browser, not part of the API contract — the server has
 * no second copy of the password to compare against, and adding one would put a
 * field on the wire that no endpoint reads.
 *
 * The confirmation borrows `changePasswordRequestSchema.shape.currentPassword`
 * (a plain non-empty, bounded string) rather than `passwordSchema`. That is
 * deliberate: re-running the POLICY here would report "too short" against the
 * confirmation box when the real problem is a typo, and the box is meaningless
 * on its own anyway — the equality check below is the only rule it has.
 */
const resetPasswordFormSchema = resetPasswordRequestSchema
  .extend({ [CONFIRM_FIELD]: changePasswordRequestSchema.shape.currentPassword })
  .superRefine((values, ctx) => {
    if (values.newPassword === values.confirmNewPassword) return;
    ctx.addIssue({
      code: 'custom',
      path: [CONFIRM_FIELD],
      // Replaced by translated copy in `localizeErrors` before anything renders —
      // zod's own prose is untranslated English and is never shown (docs/011).
      message: 'passwords must match',
    });
  });

type ResetPasswordFormValues = ResetPasswordRequest & { confirmNewPassword: string };

function isBranch(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Rewrites every message the resolver produced into this app's translated copy.
 *
 * Same technique as platform-admin's `employee-contact-form-page.tsx`: zod's messages are untranslated
 * English prose, so none is ever rendered. The mismatch issue is the one case
 * worth its own wording — it is the only rule the user can fix without knowing
 * the password policy — and it is identifiable because `superRefine` raises it
 * as `custom` on the confirmation field.
 */
function localizeErrors(errors: unknown, validationMessage: string, mismatchMessage: string): void {
  if (!isBranch(errors)) return;
  for (const [name, error] of Object.entries(errors)) {
    if (!isBranch(error)) continue;
    error['message'] =
      name === CONFIRM_FIELD && error['type'] === 'custom' ? mismatchMessage : validationMessage;
  }
}

/**
 * Step two of password recovery: redeem the emailed code.
 *
 * Public, like `/forgot-password` — registered outside `RequireAuth`, because
 * the user cannot sign in yet by definition.
 *
 * `email` is prefilled from the `?email=` the previous screen appends, and stays
 * editable: it is a convenience, never a capability, since a code only works
 * against the address it was issued for.
 *
 * Its states: idle, submitting (`formState.isSubmitting`, so a double-click
 * cannot burn the code twice), error — one translated message for the error CODE
 * the API returned, `INVALID_RESET_CODE` and `PASSWORD_POLICY` being the two
 * that matter — and done, which offers the way on to sign in.
 */
export function ResetPasswordPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const [searchParams] = useSearchParams();
  const { client } = parseSignInRequest(searchParams);

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');
  const mismatchMessage = t('resetPassword.mismatch');

  const resolver = useMemo<Resolver<ResetPasswordFormValues>>(() => {
    const base = zodResolver(resetPasswordFormSchema);
    return async (values, context, options) => {
      const result = await base({ ...values, email: values.email.trim() }, context, {
        criteriaMode: options.criteriaMode,
        fields: options.fields,
        shouldUseNativeValidation: options.shouldUseNativeValidation,
      });
      localizeErrors(result.errors, validationMessage, mismatchMessage);
      return result;
    };
  }, [validationMessage, mismatchMessage]);

  const form = useForm<ResetPasswordFormValues>({
    resolver,
    defaultValues: {
      email: searchParams.get(RESET_EMAIL_PARAM) ?? '',
      code: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values: ResetPasswordFormValues): Promise<void> => {
    setErrorCode(null);
    try {
      // The confirmation box never goes on the wire — no endpoint reads it.
      await api.auth.resetPassword({
        email: values.email.trim(),
        code: values.code.trim(),
        newPassword: values.newPassword,
      });
      setIsDone(true);
    } catch (error: unknown) {
      setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        {isDone ? (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('resetPassword.title')}
              </h1>
            </CardHeader>
            <CardContent>
              <Alert variant="success">
                <AlertTitle>{t('resetPassword.doneTitle')}</AlertTitle>
                <AlertDescription>{t('resetPassword.doneDescription')}</AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter>
              <Button asChild className="w-full">
                <Link to={centralLoginPath(client)}>{t('resetPassword.doneSignIn')}</Link>
              </Button>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('resetPassword.title')}
              </h1>
              <CardDescription>{t('resetPassword.description')}</CardDescription>
            </CardHeader>

            <Form {...form}>
              <form
                noValidate
                onSubmit={(event) => {
                  void onSubmit(event);
                }}
              >
                <CardContent className="flex flex-col gap-4">
                  {errorCode === null ? null : (
                    <p role="alert" className="text-danger text-sm text-pretty">
                      {errorMessage(t, errorCode)}
                    </p>
                  )}

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('resetPassword.emailLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            inputMode="email"
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck={false}
                            width="full"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('resetPassword.codeLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoCapitalize="none"
                            spellCheck={false}
                            width="sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('resetPassword.newPasswordLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="new-password"
                            width="full"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>{t('resetPassword.passwordHint')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmNewPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('resetPassword.confirmLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="new-password"
                            width="full"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>

                <CardFooter className="flex flex-col items-stretch gap-3">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                  >
                    {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
                  </Button>
                  <Button asChild variant="link" className="w-full">
                    <Link to={forgotPasswordPath(client)}>{t('resetPassword.requestNewCode')}</Link>
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </>
        )}
      </Card>
    </AuthCardLayout>
  );
}
